<template>
  <div ref="pageContainer"
    class="min-h-screen bg-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
    <div class="absolute inset-0 pointer-events-none">
      <img v-for="(bee, index) in bees" :key="index" :src="beeImage" alt="Bee"
        class="absolute w-12 h-12 transition-none" :style="{
          left: bee.x + 'px',
          top: bee.y + 'px',
          transform: `scaleX(${bee.vx > 0 ? 1 : -1})`,
        }" />
    </div>

    <div ref="contentContainer" class="max-w-md w-full space-y-8 relative z-10">
      <div class="bg-white rounded-lg shadow-lg p-8 border border-creme-300">
        <div>
          <h2 class="mt-6 text-center font-header text-[1.5rem] font-bold text-primary">
            Register
          </h2>
          <p class="mt-2 text-center font-body text-[1.125rem] text-gray-900 leading-[1.75]">
            Create your account
          </p>
        </div>

        <form class="mt-8 space-y-6" @submit.prevent="handleRegister">
          <div v-if="error"
            class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md font-body text-[1.125rem] leading-[1.75]">
            {{ error }}
          </div>
          <div class="space-y-4">
            <div>
              <label for="firstName" class="block text-sm font-header font-medium text-primary mb-1">
                First Name <span class="text-red-600">*</span>
              </label>
              <input id="firstName" v-model="firstName" type="text" required
                class="appearance-none relative block w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder-gray-500 focus:z-10 font-body text-[1.125rem] leading-[1.75]"
                placeholder="Your first name" />
            </div>

            <div>
              <label for="lastName" class="block text-sm font-header font-medium text-primary mb-1">
                Last Name <span class="text-red-600">*</span>
              </label>
              <input id="lastName" v-model="lastName" type="text" required
                class="appearance-none relative block w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder-gray-500 focus:z-10 font-body text-[1.125rem] leading-[1.75]"
                placeholder="Your last name" />
            </div>

            <div>
              <label for="phoneNumber" class="block text-sm font-header font-medium text-primary mb-1">
                Phone Number
              </label>
              <input id="phoneNumber" v-model="phoneNumber" type="tel"
                class="appearance-none relative block w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder-gray-500 focus:z-10 font-body text-[1.125rem] leading-[1.75]"
                placeholder="(123) 456-7890" />
            </div>

            <div>
              <label for="email" class="block text-sm font-header font-medium text-primary mb-1">
                Email address <span class="text-red-600">*</span>
              </label>
              <input id="email" v-model="email" type="email" required
                class="appearance-none relative block w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder-gray-500 focus:z-10 font-body text-[1.125rem] leading-[1.75]"
                placeholder="your@email.com" />
            </div>

            <div>
              <label for="password" class="block text-sm font-header font-medium text-primary mb-1">
                Password <span class="text-red-600">*</span>
              </label>
              <input id="password" v-model="password" type="password" required @input="passwordTouched = true" :class="[
                'appearance-none relative block w-full px-4 py-3 bg-white text-black border rounded-md focus:outline-none focus:ring-1 placeholder-gray-500 focus:z-10 font-body text-[1.125rem] leading-[1.75]',
                passwordError && passwordTouched ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-black focus:border-black'
              ]" placeholder="Create a password" />
              <p v-if="passwordError && passwordTouched"
                class="mt-1 font-body text-[1.125rem] text-red-600 leading-[1.75]">
                {{ passwordError }}
              </p>
            </div>

            <div>
              <label for="confirmPassword" class="block text-sm font-header font-medium text-primary mb-1">
                Confirm Password <span class="text-red-600">*</span>
              </label>
              <input id="confirmPassword" v-model="confirmPassword" type="password" required
                class="appearance-none relative block w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder-gray-500 focus:z-10 font-body text-[1.125rem] leading-[1.75]"
                placeholder="Confirm your password" />
            </div>
          </div>

          <div>
            <button type="submit" :disabled="loading"
              class="group relative w-full flex justify-center py-2 px-4 border border-transparent font-header text-[1.125rem] font-medium rounded-md text-white bg-accent hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {{ loading ? 'Creating account...' : 'Create account' }}
            </button>
          </div>

          <div class="text-center">
            <p class="font-body text-[1.125rem] text-gray-900 leading-[1.75]">
              Already have an account?
              <a href="/login" class="font-header font-medium text-accent hover:text-green-700 transition-colors">
                Login
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { authService } from '../services/auth'

const pageContainer = ref(null)
const contentContainer = ref(null)
const firstName = ref('')
const lastName = ref('')
const phoneNumber = ref('')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const error = ref('')
const loading = ref(false)
const passwordTouched = ref(false)
const beeImage = '/logos/Icon Logo/No Background Black Logo Icon Cropped.png'

const bees = ref([
  { x: 100, y: 150, vx: 0.8, vy: 0.6 },
  { x: 300, y: 200, vx: -0.7, vy: 0.5 },
  { x: 500, y: 100, vx: 0.9, vy: -0.8 },
  { x: 700, y: 300, vx: -0.6, vy: -0.7 },
  { x: 900, y: 250, vx: 0.75, vy: 0.65 },
])

let animationFrame = null

const updateBees = () => {
  if (!pageContainer.value) {
    animationFrame = requestAnimationFrame(updateBees)
    return
  }

  const containerRect = pageContainer.value.getBoundingClientRect()
  const containerLeft = containerRect.left
  const containerRight = containerRect.right
  const containerTop = containerRect.top
  const containerBottom = containerRect.bottom
  const containerWidth = containerRight - containerLeft
  const containerHeight = containerBottom - containerTop

  const cardCenterX = containerLeft + containerWidth / 2
  const cardCenterY = containerTop + containerHeight / 2
  const cardLeft = cardCenterX - 224
  const cardRight = cardCenterX + 224
  const cardTop = cardCenterY - 300
  const cardBottom = cardCenterY + 300

  bees.value.forEach(bee => {
    const prevX = bee.x
    const prevY = bee.y

    bee.x += bee.vx
    bee.y += bee.vy

    if (bee.x < 0) {
      bee.x = 0
      if (bee.vx < 0) {
        bee.vx = -bee.vx
        bee.x = Math.max(0, bee.x + bee.vx * 2)
      }
    } else if (bee.x > containerWidth - 48) {
      bee.x = containerWidth - 48
      if (bee.vx > 0) {
        bee.vx = -bee.vx
        bee.x = Math.min(containerWidth - 48, bee.x + bee.vx * 2)
      }
    }

    if (bee.y < 0) {
      bee.y = 0
      if (bee.vy < 0) {
        bee.vy = -bee.vy
        bee.y = Math.max(0, bee.y + bee.vy * 2)
      }
    } else if (bee.y > containerHeight - 48) {
      bee.y = containerHeight - 48
      if (bee.vy > 0) {
        bee.vy = -bee.vy
        bee.y = Math.min(containerHeight - 48, bee.y + bee.vy * 2)
      }
    }

    const beeCenterX = containerLeft + bee.x + 24
    const beeCenterY = containerTop + bee.y + 24
    const prevBeeCenterX = containerLeft + prevX + 24
    const prevBeeCenterY = containerTop + prevY + 24
    const wasInsideCard = prevBeeCenterX > cardLeft && prevBeeCenterX < cardRight && prevBeeCenterY > cardTop && prevBeeCenterY < cardBottom

    if (
      beeCenterX > cardLeft &&
      beeCenterX < cardRight &&
      beeCenterY > cardTop &&
      beeCenterY < cardBottom
    ) {
      if (!wasInsideCard) {
        const distanceFromLeft = beeCenterX - cardLeft
        const distanceFromRight = cardRight - beeCenterX
        const distanceFromTop = beeCenterY - cardTop
        const distanceFromBottom = cardBottom - beeCenterY

        const minDistance = Math.min(distanceFromLeft, distanceFromRight, distanceFromTop, distanceFromBottom)

        if (minDistance === distanceFromLeft || minDistance === distanceFromRight) {
          if ((minDistance === distanceFromLeft && bee.vx < 0) || (minDistance === distanceFromRight && bee.vx > 0)) {
            bee.vx = -bee.vx
            bee.x = prevX + bee.vx * 2
          }
        } else {
          if ((minDistance === distanceFromTop && bee.vy < 0) || (minDistance === distanceFromBottom && bee.vy > 0)) {
            bee.vy = -bee.vy
            bee.y = prevY + bee.vy * 2
          }
        }
      }
    }
  })

  animationFrame = requestAnimationFrame(updateBees)
}

const handleResize = () => {
  if (!pageContainer.value) return

  const containerRect = pageContainer.value.getBoundingClientRect()
  const containerWidth = containerRect.width
  const containerHeight = containerRect.height

  bees.value.forEach(bee => {
    bee.x = Math.min(bee.x, containerWidth - 48)
    bee.y = Math.min(bee.y, containerHeight - 48)
  })
}

onMounted(() => {
  if (!pageContainer.value) return

  const containerRect = pageContainer.value.getBoundingClientRect()
  const containerWidth = containerRect.width
  const containerHeight = containerRect.height

  bees.value = [
    { x: containerWidth * 0.1, y: containerHeight * 0.2, vx: 0.8, vy: 0.6 },
    { x: containerWidth * 0.3, y: containerHeight * 0.3, vx: -0.7, vy: 0.5 },
    { x: containerWidth * 0.5, y: containerHeight * 0.15, vx: 0.9, vy: -0.8 },
    { x: containerWidth * 0.7, y: containerHeight * 0.4, vx: -0.6, vy: -0.7 },
    { x: containerWidth * 0.9, y: containerHeight * 0.3, vx: 0.75, vy: 0.65 },
  ]

  updateBees()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  if (animationFrame) {
    cancelAnimationFrame(animationFrame)
  }
  window.removeEventListener('resize', handleResize)
})

const validatePassword = (pwd) => {
  if (!pwd) return null

  if (pwd.length < 8) {
    return 'Password must be at least 8 characters'
  }

  const hasUpperCase = /[A-Z]/.test(pwd)
  const hasLowerCase = /[a-z]/.test(pwd)
  const hasNumber = /[0-9]/.test(pwd)
  const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)

  if (!hasUpperCase) {
    return 'Password must contain at least one uppercase letter'
  }
  if (!hasLowerCase) {
    return 'Password must contain at least one lowercase letter'
  }
  if (!hasNumber) {
    return 'Password must contain at least one number'
  }
  if (!hasSpecialChar) {
    return 'Password must contain at least one special character'
  }

  return null
}

const passwordError = computed(() => validatePassword(password.value))

const handleRegister = async () => {
  error.value = ''
  passwordTouched.value = true

  if (password.value !== confirmPassword.value) {
    error.value = 'Passwords do not match'
    return
  }

  if (passwordError.value) {
    error.value = passwordError.value
    return
  }

  loading.value = true

  try {
    const result = await authService.register(email.value, password.value, firstName.value, lastName.value, phoneNumber.value || null)

    if (result.success) {
      window.location.href = '/message?type=verify-email'
    } else {
      error.value = typeof result.error === 'string' ? result.error : 'Registration failed. Please try again.'
    }
  } catch (err) {
    error.value = 'An unexpected error occurred. Please try again.'
  } finally {
    loading.value = false
  }
}

</script>
